// Autenticació del portal mòbil /Control_Horari, totalment aïllada de
// l'ERP general (utils/auth.js, UserContext.js): claus pròpies de
// localStorage i un access/refresh token propis (backend: ControlHorariAuth.php).
// No compartir aquest mòdul amb la resta de pantalles de l'ERP.

import { API_URL } from "../../utils/config";

const KEY_ACCESS = "ch_accessToken";
const KEY_REFRESH = "ch_refreshToken";
const KEY_TERMINAL = "ch_uuidTerminal";
const KEY_EMPLEAT = "ch_empleat";

// UUID v4. S'utilitza tant pel Terminal (persistent) com pels Events
// (un de nou cada fitxatge).
export const chGenerarUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// UUIDTerminal persistent per aquest navegador/dispositiu (CURRENT_TASK.md
// §11): es genera només la primera vegada i no torna a canviar.
export const chObtenirUuidTerminal = () => {
  let uuid = localStorage.getItem(KEY_TERMINAL);
  if (!uuid) {
    uuid = chGenerarUUID();
    localStorage.setItem(KEY_TERMINAL, uuid);
  }
  return uuid;
};

// Etiqueta simple i estable del dispositiu (no és cap identificador físic,
// només per mostrar-la a l'administració: CURRENT_TASK.md §4.1).
export const chNomTerminal = () => {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/Android/i.test(ua)) return "Mòbil Android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "Mòbil iOS";
  return "Navegador web";
};

export const chGuardarSessio = ({ accessToken, refreshToken, empleat }) => {
  localStorage.setItem(KEY_ACCESS, accessToken);
  if (refreshToken) localStorage.setItem(KEY_REFRESH, refreshToken);
  if (empleat) localStorage.setItem(KEY_EMPLEAT, JSON.stringify(empleat));
};

const chActualitzarAccessToken = (accessToken) => {
  localStorage.setItem(KEY_ACCESS, accessToken);
};

// No s'esborra ch_uuidTerminal: el Terminal segueix sent el mateix
// dispositiu encara que es tanqui la sessió (CURRENT_TASK.md §11).
export const chNetejarSessio = () => {
  localStorage.removeItem(KEY_ACCESS);
  localStorage.removeItem(KEY_REFRESH);
  localStorage.removeItem(KEY_EMPLEAT);
};

// Comprovació optimista: si hi ha un refresh token guardat, es considera
// que "hi ha sessió" (encara que hagi caducat/estigui revocat; en aquest
// cas la primera crida autenticada ho detectarà i farà fora l'Usuari).
export const chTeSessio = () => !!localStorage.getItem(KEY_REFRESH);

export const chEmpleatDesatLocalment = () => {
  try {
    const raw = localStorage.getItem(KEY_EMPLEAT);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Llançat quan no s'ha pogut renovar la sessió (refresh token caducat,
// revocat o inexistent): qui el rebi ha de netejar i tornar al login.
export class ChSessioCaducadaError extends Error {}

let renovacioEnCurs = null;

async function chRenovarAccessToken() {
  if (renovacioEnCurs) return renovacioEnCurs;

  renovacioEnCurs = (async () => {
    const refreshToken = localStorage.getItem(KEY_REFRESH);
    if (!refreshToken) {
      throw new ChSessioCaducadaError("Sense refresh token.");
    }
    const resposta = await fetch(`${API_URL}/ControlHorariRefresh.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    const dades = await resposta.json().catch(() => null);
    if (!resposta.ok || !dades || !dades.success) {
      chNetejarSessio();
      throw new ChSessioCaducadaError((dades && dades.message) || "No s'ha pogut renovar la sessió.");
    }
    chActualitzarAccessToken(dades.accessToken);
    return dades.accessToken;
  })();

  try {
    return await renovacioEnCurs;
  } finally {
    renovacioEnCurs = null;
  }
}

// Fetch autenticat del portal Control Horari: afegeix l'access token i, si
// el backend respon 401 (caducat), intenta renovar-lo una vegada amb el
// refresh token abans de rendir-se (CURRENT_TASK.md §8).
export const chFetch = async (path, options = {}) => {
  const enviar = () => {
    const token = localStorage.getItem(KEY_ACCESS);
    return fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  };

  let resposta = await enviar();
  if (resposta.status === 401) {
    await chRenovarAccessToken(); // Llança ChSessioCaducadaError si no es pot renovar.
    resposta = await enviar();
    if (resposta.status === 401) {
      chNetejarSessio();
      throw new ChSessioCaducadaError("La sessió ha caducat.");
    }
  }
  return resposta;
};
